/* =====================================================================
   Wise — Assistant de discussion · assets/wh-chat.js
   ---------------------------------------------------------------------
   Complète assets/wise.js SANS le modifier ni dupliquer sa logique :
   - l'ouverture / fermeture reste celle de wise.js (#chat-win.is-on,
     #chat.is-open, #chat-btn[aria-expanded]) : on l'OBSERVE seulement ;
     le bouton « fermer » de l'en-tête déclenche le lanceur de wise.js ;
   - Échap est déjà géré par wise.js (fermeture + focus sur le lanceur) ;
   - les réponses, la recherche catalogue et l'indicateur de saisie
     restent ceux de wise.js.
   Ajouts : focus dans la fenêtre à l'ouverture (sans clavier virtuel
   qui surgit sur écran tactile) et retour au lanceur à la fermeture ;
   piège de focus, aria-modal et verrou du défilement de la page en mode
   feuille (téléphone) ; hauteur calée sur la zone visible quand le
   clavier s'ouvre (visualViewport) ; défilement vers le dernier message ;
   ARIA des messages générés (avatar, heure, saisie masqués) ; vignettes
   produits allégées et prix à la virgule ; pastille masquée après la
   première ouverture (session) ; réponses rapides (bords en fondu,
   molette) ; bouton d'envoi actif seulement avec du texte ; lanceur
   remonté au-dessus de la barre d'achat collante.
   Idempotent, sans dépendance, inoffensif si le chat est désactivé.
   ===================================================================== */
(function () {
  'use strict';

  var doc = document;
  var html = doc.documentElement;
  /* Même requête que la section 8 de wh-chat.css (mode feuille). */
  var SHEET = '(max-width: 767.98px), (max-width: 1023.98px) and (max-height: 560px)';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var SEEN_KEY = 'whc_seen';

  function media(query) {
    try { return window.matchMedia ? window.matchMedia(query) : null; } catch (e) { return null; }
  }
  function matches(mq) { return !!(mq && mq.matches); }
  function onChange(mq, fn) {
    if (!mq) return;
    if (mq.addEventListener) mq.addEventListener('change', fn);
    else if (mq.addListener) mq.addListener(fn);
  }
  function each(list, fn) {
    if (list) Array.prototype.forEach.call(list, fn);
  }
  function focusEl(el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
  }
  function wasSeen() {
    try { return window.sessionStorage.getItem(SEEN_KEY) === '1'; } catch (e) { return false; }
  }
  function markSeen() {
    try { window.sessionStorage.setItem(SEEN_KEY, '1'); } catch (e) { /* stockage indisponible */ }
  }
  function frame(fn) {
    return window.requestAnimationFrame ? window.requestAnimationFrame(fn) : setTimeout(fn, 16);
  }
  function cancelFrame(id) {
    if (window.cancelAnimationFrame) window.cancelAnimationFrame(id); else clearTimeout(id);
  }

  function init() {
    var chat = doc.getElementById('chat');
    var win = doc.getElementById('chat-win');
    var btn = doc.getElementById('chat-btn');
    if (!chat || !win || !btn || chat.whcReady || !('MutationObserver' in window)) return;
    chat.whcReady = true;
    chat.classList.add('whc-js');

    var msgs = doc.getElementById('chat-msgs');
    var form = doc.getElementById('chat-ask');
    var input = doc.getElementById('chat-ask-input');
    var quick = chat.querySelector('.chat__quick');
    var sheetMq = media(SHEET);
    var touchMq = media('(hover: none) and (pointer: coarse)');
    var vv = window.visualViewport || null;
    var lang = (html.getAttribute('lang') || 'fr').toLowerCase().slice(0, 2);
    var labelOpen = btn.getAttribute('data-whc-label-open') || btn.getAttribute('aria-label') || '';
    var labelClose = btn.getAttribute('data-whc-label-close') || labelOpen;

    var isOpen = false;
    var locked = false;
    var guard = false;
    var guardTimer = 0;
    var focusTimer = 0;
    var vvFrame = 0;
    var vvHeight = 0;
    var edgeFrame = 0;

    function opened() { return win.classList.contains('is-on'); }
    function sheet() { return matches(sheetMq); }

    /* ---------- Dernier message visible ---------- */
    function toEnd(smooth) {
      if (!msgs) return;
      if (smooth) { msgs.scrollTop = msgs.scrollHeight; return; }
      var before = msgs.style.scrollBehavior;
      msgs.style.scrollBehavior = 'auto';
      msgs.scrollTop = msgs.scrollHeight;
      msgs.style.scrollBehavior = before;
    }

    /* ---------- Libellé du lanceur selon l'état (aria-expanded reste à wise.js) ---------- */
    function syncLabel() {
      var text = isOpen ? labelClose : labelOpen;
      if (text && btn.getAttribute('aria-label') !== text) btn.setAttribute('aria-label', text);
    }

    /* ---------- Clavier virtuel : la feuille épouse la zone visible ---------- */
    function applyViewport() {
      vvFrame = 0;
      if (!vv || !isOpen || !sheet()) return;
      if (vv.scale && vv.scale > 1.01) return; /* zoom à deux doigts : on ne suit pas */
      var h = Math.round(vv.height);
      chat.style.setProperty('--whc-vvh', h + 'px');
      chat.style.setProperty('--whc-vvt', Math.round(vv.offsetTop) + 'px');
      chat.classList.add('whc-vv');
      chat.classList.toggle('whc-kb', window.innerHeight - h > 140);
      if (Math.abs(h - vvHeight) > 40) toEnd(false);
      vvHeight = h;
    }
    function queueViewport() {
      if (!vvFrame) vvFrame = frame(applyViewport);
    }
    function clearViewport() {
      if (vvFrame) { cancelFrame(vvFrame); vvFrame = 0; }
      vvHeight = 0;
      chat.classList.remove('whc-vv');
      chat.classList.remove('whc-kb');
      chat.style.removeProperty('--whc-vvh');
      chat.style.removeProperty('--whc-vvt');
    }
    if (vv) {
      vv.addEventListener('resize', queueViewport);
      vv.addEventListener('scroll', queueViewport);
    }

    /* ---------- Mode feuille : modale, verrou du défilement, zone visible ---------- */
    function setSheet(on) {
      win.setAttribute('aria-modal', on ? 'true' : 'false');
      if (on && !locked) { html.classList.add('whc-lock'); locked = true; }
      if (!on && locked) { html.classList.remove('whc-lock'); locked = false; }
      if (on) applyViewport(); else clearViewport();
    }

    /* ---------- Focus à l'ouverture, sans clavier qui surgit ----------
       wise.js donne le focus au champ 320 ms après l'ouverture. Sur écran
       tactile, ce focus ouvrirait le clavier par-dessus les réponses rapides :
       le champ passe en lecture seule le temps de l'ouverture et le focus va
       sur la fenêtre (annoncée par les lecteurs d'écran). Un toucher sur le
       champ lève aussitôt la protection. */
    function releaseGuard() {
      guard = false;
      clearTimeout(guardTimer);
      if (input && input.readOnly) input.readOnly = false;
    }
    if (input) {
      input.addEventListener('focus', function () {
        if (!guard) return;
        focusEl(win);
        releaseGuard();
      });
      input.addEventListener('pointerdown', releaseGuard);
      input.addEventListener('mousedown', releaseGuard);
      input.addEventListener('touchstart', releaseGuard, { passive: true });
    }

    /* ---------- Ouverture / fermeture : on suit l'état posé par wise.js ---------- */
    function onOpen() {
      markSeen();
      chat.classList.add('whc-seen');
      syncLabel();
      setSheet(sheet());
      clearTimeout(focusTimer);
      if (input && matches(touchMq)) {
        guard = true;
        input.readOnly = true;
        clearTimeout(guardTimer);
        guardTimer = setTimeout(releaseGuard, 1500);
        focusEl(win);
      } else {
        /* Filet de sécurité si le focus de wise.js n'a pas eu lieu. */
        focusTimer = setTimeout(function () {
          var active = doc.activeElement;
          if (isOpen && (!active || active === doc.body || active === btn)) focusEl(win);
        }, 600);
      }
      frame(function () { toEnd(false); edges(); });
    }
    function onClose() {
      syncLabel();
      setSheet(false);
      releaseGuard();
      clearTimeout(focusTimer);
      var active = doc.activeElement;
      if (!active || active === doc.body || win.contains(active)) focusEl(btn);
    }
    function sync() {
      var now = opened();
      if (now === isOpen) return;
      isOpen = now;
      if (now) onOpen(); else onClose();
    }
    new MutationObserver(sync).observe(win, { attributes: true, attributeFilter: ['class'] });
    onChange(sheetMq, function () { if (isOpen) setSheet(sheet()); });

    /* Bouton « fermer » de l'en-tête : on passe par le lanceur de wise.js. */
    each(chat.querySelectorAll('[data-whc-close]'), function (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (opened()) btn.click();
      });
    });

    /* ---------- Piège de focus (mode feuille uniquement) ---------- */
    function focusables() {
      var out = [];
      each(win.querySelectorAll(FOCUSABLE), function (el) {
        if (el.offsetWidth || el.offsetHeight || el.getClientRects().length) out.push(el);
      });
      return out;
    }
    doc.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !isOpen || !sheet()) return;
      var items = focusables();
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      var active = doc.activeElement;
      var inside = win.contains(active);
      if (e.shiftKey && (!inside || active === first || active === win)) {
        e.preventDefault();
        focusEl(last);
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault();
        focusEl(first);
      }
    });

    /* ---------- Messages générés par wise.js : ARIA, vignettes, prix ---------- */
    var decimalComma = lang === 'fr' || lang === 'es';
    function lighten(img) {
      var src = img.getAttribute('src') || '';
      if (/(?:cdn\.shopify\.com|\/cdn\/shop\/)/.test(src) && !/[?&]width=/.test(src)) {
        img.setAttribute('src', src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=160');
      }
      img.setAttribute('width', '56');
      img.setAttribute('height', '56');
      img.setAttribute('decoding', 'async');
    }
    function tidy(row) {
      if (!row || row.nodeType !== 1 || row.whcTidy) return;
      row.whcTidy = true;
      /* Indicateur de saisie : décoratif, rien à annoncer. */
      if (row.querySelector('.chat__typing')) {
        row.setAttribute('aria-hidden', 'true');
        return;
      }
      each(row.querySelectorAll('.chat__mini-av, .chat__time'), function (el) {
        el.setAttribute('aria-hidden', 'true');
      });
      each(row.querySelectorAll('.chat__prod img'), function (img) {
        lighten(img);
        if (!img.complete) {
          img.addEventListener('load', function () {
            if (msgs && msgs.lastElementChild === row) toEnd(true);
          });
        }
      });
      if (decimalComma) {
        each(row.querySelectorAll('.chat__prod-p'), function (price) {
          var text = price.textContent;
          var next = text.replace(/(\d)\.(\d{2})(?!\d)/, '$1,$2');
          if (next !== text) price.textContent = next;
        });
      }
    }
    if (msgs) {
      each(msgs.children, tidy);
      new MutationObserver(function (records) {
        each(records, function (record) { each(record.addedNodes, tidy); });
      }).observe(msgs, { childList: true });
    }

    /* ---------- Réponses rapides : bords en fondu, molette horizontale ---------- */
    function edges() {
      edgeFrame = 0;
      if (!quick) return;
      var max = quick.scrollWidth - quick.clientWidth;
      var x = Math.abs(quick.scrollLeft);
      var state = max <= 2 ? 'none' : (x <= 2 ? 'start' : (x >= max - 2 ? 'end' : 'mid'));
      if (quick.getAttribute('data-whc-edge') !== state) quick.setAttribute('data-whc-edge', state);
    }
    function queueEdges() {
      if (!edgeFrame) edgeFrame = frame(edges);
    }
    if (quick) {
      quick.addEventListener('scroll', queueEdges, { passive: true });
      window.addEventListener('resize', queueEdges);
      quick.addEventListener('wheel', function (e) {
        if (e.ctrlKey || Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
        if (quick.scrollWidth - quick.clientWidth <= 2) return;
        var before = quick.scrollLeft;
        quick.scrollLeft = before + e.deltaY * (e.deltaMode === 1 ? 32 : 1);
        if (quick.scrollLeft !== before) e.preventDefault();
      }, { passive: false });
      edges();
    }

    /* ---------- Bouton d'envoi actif seulement avec du texte ---------- */
    function syncSend() {
      if (form && input) form.classList.toggle('whc-has-text', input.value.trim() !== '');
    }
    if (form && input) {
      input.addEventListener('input', syncSend);
      /* wise.js vide le champ dans son propre écouteur « submit ». */
      form.addEventListener('submit', function () { setTimeout(syncSend, 0); });
      syncSend();
    }

    /* ---------- Barre d'achat collante (fiche produit) : le lanceur remonte ---------- */
    var atc = doc.getElementById('sticky-atc');
    if (atc) {
      var lift = function () { chat.classList.toggle('whc-lift', atc.classList.contains('is-on')); };
      new MutationObserver(lift).observe(atc, { attributes: true, attributeFilter: ['class'] });
      lift();
    }

    /* ---------- État initial ---------- */
    if (wasSeen()) chat.classList.add('whc-seen');
    syncLabel();
    sync();
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();
})();
